import { createAppReducer, apiReducerSwitch } from 'lib/redux/reducerUtils'

import {
  DELETE_REVIEW,
  FETCH_REVIEW_SUMMARY,
  FETCH_REVIEWS,
  FETCH_REVIEWS_ORDER,
  UPLOAD_REVIEW_PHOTO,
  UPSERT_REVIEW,
} from 'actions/apiActionConstants'

import {
  API_CALL_FAILURE,
  API_CALL_REQUEST,
  API_CALL_SUCCESS,
  USER_REVIEW_SET_PHOTO_UPLOAD_PERCENTAGE,
  USER_REVIEW_SET_REVIEW_STAGE,
} from 'actions/actionConstants'

export const initialReviewState: App.ReviewState = {
  reviews: [],
  reviewStage: 'initial',
  isReviewDeleted: false,
  totalReviews: 0,
  fetchingReviews: false,
  fetchingReviewCreate: false,
  reviewCreateError: undefined,
  reviewDeleteError: undefined,
  reviewsByOrder: {},
  reviewsByOrderLoading: {},
  reviewPhotosUpload: {},
  fetchingReviewSummary: false,
  reviewSummary: undefined,
}

const apiCalls = apiReducerSwitch<App.ReviewState>({
  [FETCH_REVIEWS]: {
    request: () => ({
      fetchingReviews: true,
    }),
    success: (_, action) => {
      return {
        fetchingReviews: false,
        reviews: action.data.reviews,
        totalReviews: action.data.total,
      } },
    failure: () => ({
      fetchingReviews: false,
    }),
  },
  [FETCH_REVIEWS_ORDER]: {
    request: (state, action) => ({
      isReviewDeleted: false,
      reviewsByOrderLoading: {
        ...state.reviewsByOrderLoading,
        [action.orderId]: true,
      },
    }),
    success: (state, action) => ({
      reviewsByOrder: {
        ...state.reviewsByOrder,
        [action.orderId]: action.data.reviews,
      },
      reviewsByOrderLoading: {
        ...state.reviewsByOrderLoading,
        [action.orderId]: false,
      },
    }),
    failure: (state, action) => ({
      reviewsByOrderLoading: {
        ...state.reviewsByOrderLoading,
        [action.orderId]: false,
      },
    }),
  },
  [UPSERT_REVIEW]: {
    request: () => ({
      fetchingReviewCreate: true,
      reviewCreateError: undefined,
      isReviewDeleted: false,
    }),
    success: (state, action) => ({
      reviewCreateError: undefined,
      fetchingReviewCreate: false,
      reviewStage: action.reviewStage ? action.reviewStage : state.reviewStage,
      reviewsByOrder: {
        ...state.reviewsByOrder,
        [action.data.orderId!]: [action.data], // TODO improve when there's more then one review per order
      },
    }),
    failure: (_, action) => ({
      fetchingReviewCreate: false,
      reviewCreateError: action.error,
    }),
  },
  [DELETE_REVIEW]: {
    request: (state, action) => ({
      isReviewDeleted: false,
      reviewDeleteError: undefined,
      reviewsByOrderLoading: {
        ...state.reviewsByOrderLoading,
        [action.orderId]: true,
      },
    }),
    success: (state, action) => {
      const newReviewsByOrder = { ...state.reviewsByOrder }
      const newReviewsByOrderLoading = { ...state.reviewsByOrderLoading }

      delete newReviewsByOrder[action.orderId]
      delete newReviewsByOrderLoading[action.orderId]

      return {
        reviewsByOrder: newReviewsByOrder,
        reviewsByOrderLoading: newReviewsByOrderLoading,
        isReviewDeleted: true,
      }
    },
    failure: (state, action) => ({
      isReviewDeleted: false,
      reviewDeleteError: action.error,
      reviewsByOrderLoading: {
        ...state.reviewsByOrderLoading,
        [action.orderId]: false,
      },
    }),
  },
  [UPLOAD_REVIEW_PHOTO]: {
    request: (state, action) => ({
      reviewPhotosUpload: {
        ...state.reviewPhotosUpload,
        [action.uploadId]: {
          isUploading: true,
          error: undefined,
        },
      },
    }),
    success: (state, action) => ({
      reviewPhotosUpload: {
        ...state.reviewPhotosUpload,
        [action.uploadId]: {
          ...state.reviewPhotosUpload[action.uploadId],
          isUploading: false,
          imageId: action.data.imageId,
        },
      },
    }),
    failure: (state, action) => {
      return ({
        reviewPhotosUpload: {
          ...state.reviewPhotosUpload,
          [action.uploadId]: {
            ...state.reviewPhotosUpload[action.uploadId],
            isUploading: false,
            error: action.error,
          },
        },
      })
    },
  },
  [FETCH_REVIEW_SUMMARY]: {
    request: () => ({
      fetchingReviewSummary: true,
      reviewSummary: undefined,
    }),
    success: (_, action) => ({
      fetchingReviewSummary: false,
      reviewSummary: action.data,
    }),
    failure: () => ({
      fetchingReviewSummary: false,
    }),
  },
})

export default createAppReducer<App.ReviewState>(initialReviewState, {
  [API_CALL_REQUEST]: (state, action) => apiCalls(state, action),
  [API_CALL_FAILURE]: (state, action) => apiCalls(state, action),
  [API_CALL_SUCCESS]: (state, action) => apiCalls(state, action),
  [USER_REVIEW_SET_PHOTO_UPLOAD_PERCENTAGE]: (state, action) => ({
    reviewPhotosUpload: {
      ...state.reviewPhotosUpload,
      [action.uploadId]: {
        ...state.reviewPhotosUpload[action.uploadId],
        percentage: action.percentage,
      },
    },
  }),
  [USER_REVIEW_SET_REVIEW_STAGE]: (_, action) => ({
    reviewStage: action.reviewStage,
  }),
})
